import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Edit, BarChart3, FileText, Calendar, DollarSign } from "lucide-react";

const STATUS_COLORS: Record<string, string> = {
  draft: "badge-gray",
  active: "badge-green",
  paused: "badge-yellow",
  completed: "badge-blue",
};

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => api.get<any>(`/campaigns/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Campaign not found</p>
        <button onClick={() => navigate("/campaigns")} className="btn-secondary mt-4">Back to Campaigns</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/campaigns")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              <span className={STATUS_COLORS[campaign.status] || "badge-gray"}>{campaign.status}</span>
            </div>
            {campaign.description && <p className="text-sm text-gray-500 mt-1">{campaign.description}</p>}
          </div>
        </div>
        <button className="btn-secondary">
          <Edit className="w-4 h-4" /> Edit
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xl font-bold">${campaign.budget?.toFixed(2)}</p>
            <p className="text-xs text-gray-500">Budget</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xl font-bold">{campaign.contents?.length || 0}</p>
            <p className="text-xs text-gray-500">Content Pieces</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-xl font-bold">{campaign.scheduledPosts?.length || 0}</p>
            <p className="text-xs text-gray-500">Scheduled Posts</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xl font-bold">{campaign.adCampaigns?.length || 0}</p>
            <p className="text-xs text-gray-500">Ad Campaigns</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold">Content</h3>
            <button onClick={() => navigate(`/content?campaignId=${id}`)} className="text-sm text-blue-600 hover:text-blue-700">View All</button>
          </div>
          <div className="divide-y divide-gray-100">
            {campaign.contents?.length ? (
              campaign.contents.map((c: any) => (
                <div key={c.id} className="px-6 py-3">
                  <p className="text-sm font-medium">{c.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{c.type} · {c.status}</p>
                </div>
              ))
            ) : (
              <p className="px-6 py-6 text-sm text-gray-400 text-center">No content yet</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold">Scheduled Posts</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {campaign.scheduledPosts?.length ? (
              campaign.scheduledPosts.map((p: any) => (
                <div key={p.id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{p.content?.title || "Untitled"}</p>
                    <p className="text-xs text-gray-400">{p.platformAccount?.platform} · {p.platformAccount?.accountName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{new Date(p.scheduledAt).toLocaleString()}</p>
                    <span className={`text-xs ${p.status === "published" ? "text-green-600" : p.status === "failed" ? "text-red-600" : "text-gray-400"}`}>
                      {p.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="px-6 py-6 text-sm text-gray-400 text-center">No scheduled posts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
